import { Immutable } from "immer";
import React, { useEffect, useId, useRef, useState } from "react";
import { t } from "ttag";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import {
    sendVerificationCode,
    verifyEmailCode,
} from "../../api/emailVerification";
import { HttpError } from "../../utils/ajax";
import { trackOtpEvent } from "../../utils/analytics";

import styles from "./index.module.scss";

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

type Phase = "sending" | "input" | "verifying";

type IProps = Immutable<{
    email: string;
    /**
     * Rich-text explanation of why the code was sent, replacing the default
     * (financing) copy. Callers outside financing must pass their own -- the
     * default names financing applications.
     */
    instructions?: string;
    onVerified: () => void;
    onCancel: () => void;
}>;

const getErrorMessage = (err: unknown): string => {
    if (err instanceof HttpError) {
        if (err.status === 429) {
            return t`Too many attempts. Please wait a minute and try again.`;
        }
        if (err.status === 400) {
            return t`That code is invalid or has expired. Please double-check it, or request a new code.`;
        }
    }
    return t`An unknown error occurred. Please try again.`;
};

/**
 * Blocking step that proves the current user controls the given email
 * address before continuing.
 *
 * On mount this requests a one-time code be emailed to the address; the
 * user then enters the code to continue. If the account has already
 * verified this address (or doesn't need to — staff/CSRs), the gate calls
 * onVerified() immediately without rendering anything.
 */
export const EmailVerificationGate = (props: IProps): React.JSX.Element => {
    const [phase, setPhase] = useState<Phase>("sending");
    const [code, setCode] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [isSending, setIsSending] = useState(false);
    const inputElem = useRef<HTMLInputElement | null>(null);
    const inputID = useId();
    // Set when the user backs out to edit their info. A verify (or resend)
    // request may still be in flight at that point; its continuation must not
    // call onVerified() and submit the application after the user explicitly
    // backed out.
    const isCanceled = useRef(false);

    // Tick the resend cooldown timer down once per second.
    useEffect(() => {
        if (resendCooldown <= 0) {
            return;
        }
        const timeoutID = setTimeout(() => {
            setResendCooldown((s) => Math.max(0, s - 1));
        }, 1000);
        return () => {
            clearTimeout(timeoutID);
        };
    }, [resendCooldown]);

    const sendCode = async (isResend = false): Promise<void> => {
        setError(null);
        setIsSending(true);
        try {
            const status = await sendVerificationCode(props.email);
            if (status === "already-verified" || status === "not-required") {
                // Don't submit the application if the user backed out while
                // this request was in flight.
                if (isCanceled.current) {
                    return;
                }
                trackOtpEvent(
                    "otp_skipped",
                    "email_verification_gate",
                    "email",
                );
                props.onVerified();
                return;
            }
            trackOtpEvent(
                isResend ? "otp_code_resent" : "otp_code_sent",
                "email_verification_gate",
                "email",
            );
            // A fresh code was issued, which invalidates any prior code, so
            // clear out anything the user may have already typed.
            setCode("");
            setPhase("input");
            setResendCooldown(RESEND_COOLDOWN_SECONDS);
        } catch (err) {
            setPhase("input");
            if (err instanceof HttpError && err.status === 429) {
                // A code was probably already sent recently (e.g. the user
                // backed out and resubmitted). Let them enter that code.
                setResendCooldown(RESEND_COOLDOWN_SECONDS);
                setError(
                    t`We recently sent a code to this address. Enter that code below, or wait a minute to request a new one.`,
                );
            } else if (err instanceof HttpError && err.status === 400) {
                setError(
                    t`We couldn't send a code to that email address. Please go back and double-check it.`,
                );
            } else {
                setError(t`An unknown error occurred. Please try again.`);
            }
        } finally {
            setIsSending(false);
        }
    };

    // Request a code as soon as the gate mounts. The ref guard prevents a
    // double-send under React StrictMode's dev-mode double-effect.
    const didRequestInitialCode = useRef(false);
    useEffect(() => {
        if (didRequestInitialCode.current) {
            return;
        }
        didRequestInitialCode.current = true;
        sendCode();
    }, []);

    // Focus the code input once it appears.
    useEffect(() => {
        if (phase === "input" && inputElem.current) {
            inputElem.current.focus();
        }
    }, [phase]);

    const onSubmit = async (
        event: React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        event.preventDefault();
        event.stopPropagation();
        if (code.length !== CODE_LENGTH) {
            setError(
                t`Please enter the ${CODE_LENGTH}-digit code from the email we sent.`,
            );
            return;
        }
        trackOtpEvent("otp_code_entered", "email_verification_gate", "email");
        setPhase("verifying");
        setError(null);
        try {
            await verifyEmailCode(props.email, code);
            // Don't submit the application if the user backed out while this
            // request was in flight.
            if (isCanceled.current) {
                return;
            }
            trackOtpEvent("otp_verified", "email_verification_gate", "email");
            props.onVerified();
        } catch (err) {
            if (isCanceled.current) {
                return;
            }
            setPhase("input");
            if (err instanceof HttpError && err.status === 400) {
                trackOtpEvent(
                    "otp_verification_failed",
                    "email_verification_gate",
                    "email",
                );
            }
            setError(getErrorMessage(err));
        }
    };

    const onCodeChange = (event: React.FormEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value
            .replace(/[^0-9]/g, "")
            .slice(0, CODE_LENGTH);
        setCode(value);
    };

    if (phase === "sending") {
        return (
            <div className={styles.wrapper}>
                <p role="status">{t`Sending a verification code to your email…`}</p>
            </div>
        );
    }

    const resendLabel =
        resendCooldown > 0
            ? t`Resend code (${resendCooldown} sec)`
            : t`Resend code`;
    return (
        <form className={styles.wrapper} onSubmit={onSubmit} noValidate={true}>
            <h3 className={styles.heading}>{t`Verify your email address`}</h3>
            <p className={styles.instructions}>
                {richT(
                    props.instructions ??
                        t`To protect against fraudulent applications, we've emailed a ${CODE_LENGTH}-digit code to <strong>${props.email}</strong>. Enter it below to continue.`,
                )}
            </p>
            <label className={styles.label} htmlFor={inputID}>
                {t`Verification code`}
            </label>
            <input
                ref={inputElem}
                id={inputID}
                className={styles.codeInput}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                maxLength={CODE_LENGTH}
                value={code}
                disabled={phase === "verifying"}
                onChange={onCodeChange}
            />
            {error && (
                <p className={styles.error} role="alert">
                    {error}
                </p>
            )}
            <div className={styles.actions}>
                <button
                    type="submit"
                    className="button"
                    disabled={
                        phase === "verifying" || code.length !== CODE_LENGTH
                    }
                >
                    {phase === "verifying" ? t`Verifying…` : t`Continue`}
                </button>
            </div>
            <div className={styles.secondaryActions}>
                <button
                    type="button"
                    className={styles.linkButton}
                    disabled={
                        resendCooldown > 0 || isSending || phase === "verifying"
                    }
                    onClick={() => {
                        sendCode(true);
                    }}
                >
                    {resendLabel}
                </button>
                <button
                    type="button"
                    className={styles.linkButton}
                    disabled={phase === "verifying"}
                    onClick={() => {
                        // Mark canceled so any in-flight verify/resend
                        // continuation won't submit the application.
                        isCanceled.current = true;
                        props.onCancel();
                    }}
                >
                    {t`Edit my information`}
                </button>
            </div>
            <p className={styles.hint}>
                {t`Didn't get the email? Check your spam folder.`}
            </p>
        </form>
    );
};
